import { forwardRef, Children, ReactNode, useCallback } from "react";
import {
  ButtonProps as AriaButtonProps,
  ButtonRenderProps,
} from "react-aria-components";
import { tv, VariantProps } from "tailwind-variants";
import { UnstyledButton } from "common/components/base";
import CircleNotch from "fontawesome/regular/circle-notch.svg?react";

export const buttonStyles = tv({
  base: "flex justify-center border items-center shrink-0 rounded-full text-base font-medium [&_svg]:transition-[fill] data-[focus-visible]:outline data-[focus-visible]:outline-3",
  variants: {
    variant: {
      black: [
        "text-white [&_svg]:fill-white bg-grey-900 border-grey-900",
        "data-[hovered]:bg-grey-700",
        "data-[disabled]:border-grey-200 data-[disabled]:bg-grey-200 data-[disabled]:text-grey-400 [&_svg]:data-[disabled]:fill-grey-400",
        "data-[focus-visible]:outline-grey-900/25",
      ],
      white: [
        "bg-grey-50 border-grey-50",
        "data-[hovered]:bg-white",
        "data-[disabled]:border-grey-200 data-[disabled]:bg-grey-200 data-[disabled]:text-grey-400 [&_svg]:data-[disabled]:fill-grey-400",
        "data-[focus-visible]:outline-grey-900/25",
      ],

      green: [
        "bg-green-500 border-green-500",
        "data-[disabled]:bg-grey-200 data-[disabled]:border-grey-200 data-[disabled]:text-grey-400 [&_svg]:data-[disabled]:fill-grey-400",
        "data-[hovered]:bg-green-400",
        "data-[focus-visible]:outline-green-500/25",
      ],
      purple: [
        "text-purple-500 bg-purple-500/20 border-purple-500 [&_svg]:fill-purple-500",
        "[&_svg]:data-[disabled]:fill-grey-400 data-[disabled]:text-grey-400 data-[disabled]:bg-grey-200 data-[disabled]:border-grey-200",
        "[&_svg]:data-[hovered]:fill-white data-[hovered]:text-white data-[hovered]:bg-purple-500 ",
        "data-[focus-visible]:outline-purple-100",
      ],
      stroke: [
        "border border-grey-300 border-grey-300",
        "data-[disabled]:text-grey-400 [&_svg]:data-[disabled]:fill-grey-400 data-[disabled]:border-grey-400",
        "data-[hovered]:bg-grey-100",
        "data-[focus-visible]:outline-grey-900/25",
      ],
      transparent: [
        "border-transparent",
        "data-[disabled]:text-grey-400 [&_svg]:data-[disabled]:fill-grey-400",
        "data-[hovered]:bg-grey-100",
        "data-[focus-visible]:outline-grey-900/25",
      ],
    },
    size: {
      lg: "h-12 text-base px-6 gap-x-3.5 [&_svg]:size-4 [&_svg]:-mx-1.5",
      md: "h-11 text-sm px-5 gap-x-2.5 [&_svg]:size-3.5 [&_svg]:-mx-1",
      sm: "h-8 text-xs px-4 gap-x-2 [&_svg]:size-3 [&_svg]:-mx-1",
    },
    isIconOnly: {
      true: "justify-center p-0",
    },
  },
  compoundVariants: [
    {
      size: "sm",
      isIconOnly: true,
      class: "w-8",
    },
    {
      size: "md",
      isIconOnly: true,
      class: "w-11",
    },
    {
      size: "lg",
      isIconOnly: true,
      class: "w-12",
    },
  ],
});
export interface ButtonProps
  extends VariantProps<typeof buttonStyles>,
    AriaButtonProps {
  isLoading?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      className,
      variant,
      size,
      isDisabled,
      isLoading,
      isIconOnly,
      ...rest
    },
    ref,
  ) => {
    const loaderComponent = <CircleNotch className="animate-spin" />;

    const getChildren = useCallback(
      (renderProps: ButtonRenderProps & { defaultChildren: ReactNode }) => {
        const childrenArray = Children.toArray(
          typeof children === "function" ? children(renderProps) : children,
        );
        if (
          childrenArray.length === 1 &&
          typeof childrenArray[0] === "string" &&
          isLoading
        ) {
          return [loaderComponent, ...childrenArray];
        }
        return Children.map(childrenArray, (child) => {
          if (typeof child !== "string" && isLoading) {
            return loaderComponent;
          }
          return child;
        });
      },
      [children, isLoading],
    );

    return (
      <UnstyledButton
        {...rest}
        ref={ref}
        className={
          typeof className === "function"
            ? (renderProps) =>
                buttonStyles({
                  size,
                  variant,
                  className: className(renderProps),
                  isIconOnly,
                })
            : buttonStyles({ size, variant, className, isIconOnly })
        }
        isDisabled={isDisabled || isLoading}
      >
        {(renderProps) => getChildren(renderProps)}
      </UnstyledButton>
    );
  },
);
export { Button };
